/**
 * Shared per-100g nutritional-profile plausibility rules (Allegato XV Reg. UE
 * 1169/2011), used by every writer of a declared nutrition profile:
 * `IngredientNutritionService._assertProfileShape` (tenant/global writes),
 * `IngredientCompletionStrategy` (AI extraction, defense-in-depth so a
 * suggestion the server would reject at save time never reaches the
 * operator), the frontend nutrition panel editor, and the distributor
 * catalog importer/build scripts (`backend/prisma/*distributor-ingredient*`).
 *
 * Returns structured violation codes instead of throwing: each consumer maps
 * a code to its own error surface (a `translateError` i18n key on the
 * backend, a form-level warning on the frontend, a skip + report line in the
 * importer).
 */

/** EU labeling conversion factor (salt = sodium x 2.5); both fields are grams
 * per 100 g. */
export const SALT_FROM_SODIUM_FACTOR = 2.5;
/** Physical constant: 1 kcal = 4.184 kJ. */
export const ENERGY_KJ_PER_KCAL = 4.184;
/** +/-10% relative tolerance absorbs independent rounding of two
 * label-declared fields (salt and sodium, or kJ and kcal, are printed/rounded
 * separately on a real label and are never derived from each other here —
 * only checked for gross mismatch). Combined with a fixed absolute floor so
 * near-zero values are not flagged on rounding noise alone. */
export const CROSS_FIELD_RELATIVE_TOLERANCE = 0.1;
export const SALT_SODIUM_TOLERANCE_ABSOLUTE_G = 0.05;
export const ENERGY_TOLERANCE_ABSOLUTE_KCAL = 5;
/**
 * Absolute tolerance (grams) for the macronutrient-total plausibility
 * ceiling: fat + carbohydrate + protein + fiber + salt (+ alcohol) can never
 * plausibly exceed the declared portion by more than independent per-field
 * label rounding explains. Raised from 1 g to 8 g after auditing the
 * distributor catalog dataset (`distributor-ingredients.production.json`,
 * 6192 records): every genuine nut/seed profile in the dataset — whose
 * fields are rounded independently on the manufacturer's label, e.g. shelled
 * almonds fat 55.3 + carbohydrate 14 + protein 22 + fiber 14.5 = 105.8 g —
 * clears 8 g of slack, while every gross unit error found (salt printed in
 * mg-as-g, a misplaced decimal) still exceeds the portion by 20 g or more.
 * 1 g was rejecting real, correctly-rounded label declarations.
 */
export const MACRO_TOTAL_TOLERANCE_G = 8;

/** `actual` is coherent with `expected` when it falls within whichever is
 * larger: a relative tolerance of `expected`, or a fixed absolute floor
 * (which dominates near zero, where a relative-only check would be
 * meaninglessly tight). */
export function isNutritionCoherent(
  actual: number,
  expected: number,
  absoluteTolerance: number,
): boolean {
  const allowedDelta = Math.max(
    Math.abs(expected) * CROSS_FIELD_RELATIVE_TOLERANCE,
    absoluteTolerance,
  );
  return Math.abs(actual - expected) <= allowedDelta;
}

export const NUTRITION_BASE_PLAUSIBILITY_CODES = [
  'SATURATED_FAT_EXCEEDS_FAT',
  'SUGARS_EXCEED_CARBOHYDRATE',
  'POLYOLS_EXCEED_CARBOHYDRATE',
  'SALT_SODIUM_MISMATCH',
  'ENERGY_KJ_KCAL_MISMATCH',
  'MACRO_TOTAL_EXCEEDS_BASE',
] as const;

export type NutritionBasePlausibilityCode = (typeof NUTRITION_BASE_PLAUSIBILITY_CODES)[number];

export interface NutritionPlausibilityViolation<TCode extends string = string> {
  code: TCode;
  message: string;
}

export type NutritionBasePlausibilityViolation =
  NutritionPlausibilityViolation<NutritionBasePlausibilityCode>;

/** Per-100g declared values a base plausibility check needs. Optional fields
 * follow the DTO convention: absent/null means "not declared", never an
 * implicit zero — the checks below default them to 0 only for the sum/energy
 * arithmetic, never to decide whether a rule applies. */
export interface NutritionBasePlausibilityInput {
  /** Defaults to 100 (the only base every current caller declares). */
  baseQuantityG?: number;
  energyKj: number;
  energyKcal: number;
  fat: number;
  saturatedFat: number;
  carbohydrate: number;
  sugars: number;
  fiber?: number | null;
  protein: number;
  salt: number;
  sodium?: number | null;
  alcohol?: number | null;
  polyols?: number | null;
}

/**
 * Cross-field rules over a per-100g profile: saturatedFat<=fat,
 * sugars<=carbohydrate, polyols<=carbohydrate, salt/sodium coherence (only
 * when sodium is declared), kJ/kcal coherence, and an overall plausible
 * macronutrient total. Evaluates every rule (does not short-circuit) so a
 * caller can report every violation, or take the first one to preserve a
 * single-error UX. Never derives one field from another: both stay
 * user/AI-declared values, this only rejects an internally contradictory or
 * implausible pair/total.
 */
export function checkNutritionBasePlausibility(
  input: NutritionBasePlausibilityInput,
): NutritionBasePlausibilityViolation[] {
  const violations: NutritionBasePlausibilityViolation[] = [];
  const baseQuantityG = input.baseQuantityG ?? 100;

  if (input.saturatedFat > input.fat) {
    violations.push({
      code: 'SATURATED_FAT_EXCEEDS_FAT',
      message: 'Saturated fat cannot exceed total fat',
    });
  }
  if (input.sugars > input.carbohydrate) {
    violations.push({
      code: 'SUGARS_EXCEED_CARBOHYDRATE',
      message: 'Sugars cannot exceed total carbohydrate',
    });
  }
  // Polyols (sugar alcohols, e.g. xylitol, erythritol) are a subset of total
  // carbohydrate on a label, exactly like sugars — same shape of check.
  if (input.polyols != null && input.polyols > input.carbohydrate) {
    violations.push({
      code: 'POLYOLS_EXCEED_CARBOHYDRATE',
      message: 'Polyols cannot exceed total carbohydrate',
    });
  }
  if (input.sodium != null) {
    const expectedSalt = input.sodium * SALT_FROM_SODIUM_FACTOR;
    if (!isNutritionCoherent(input.salt, expectedSalt, SALT_SODIUM_TOLERANCE_ABSOLUTE_G)) {
      violations.push({
        code: 'SALT_SODIUM_MISMATCH',
        message:
          'Declared salt and sodium are not mutually coherent (expected salt ~= sodium x 2.5)',
      });
    }
  }
  const expectedKcal = input.energyKj / ENERGY_KJ_PER_KCAL;
  if (!isNutritionCoherent(input.energyKcal, expectedKcal, ENERGY_TOLERANCE_ABSOLUTE_KCAL)) {
    violations.push({
      code: 'ENERGY_KJ_KCAL_MISMATCH',
      message: 'Declared kcal is not coherent with declared kJ (expected kcal ~= kJ / 4.184)',
    });
  }
  const macroTotal =
    input.fat +
    input.carbohydrate +
    input.protein +
    (input.fiber ?? 0) +
    input.salt +
    (input.alcohol ?? 0);
  if (macroTotal > baseQuantityG + MACRO_TOTAL_TOLERANCE_G) {
    violations.push({
      code: 'MACRO_TOTAL_EXCEEDS_BASE',
      message: `Macronutrient total (${macroTotal.toFixed(2)} g) implausibly exceeds the declared ${baseQuantityG} g portion`,
    });
  }
  return violations;
}

// --- Catalog-import stricter checks -----------------------------------------
//
// An additional, opt-in layer used only when curating the GLOBAL distributor
// catalog (`backend/prisma/import-distributor-ingredients.ts` and
// `build-distributor-ingredient-dataset.ts`): these rules are tighter than a
// single tenant's label entry needs and are not applied to
// `_assertProfileShape`/the AI extraction strategy/the frontend editor, which
// only ever see one already-plausible declaration at a time. The catalog
// import instead ingests thousands of third-party rows in bulk, where a
// stricter energy/fiber/salt sanity net is worth the (documented) noise.

export const CATALOG_IMPORT_PLAUSIBILITY_CODES = [
  'FIBER_IMPLAUSIBLE',
  'SALT_IMPLAUSIBLE_UNVERIFIED',
  'ENERGY_MACRO_MISMATCH',
] as const;

export type CatalogImportPlausibilityCode = (typeof CATALOG_IMPORT_PLAUSIBILITY_CODES)[number];

export type CatalogImportPlausibilityViolation =
  NutritionPlausibilityViolation<CatalogImportPlausibilityCode>;

/** Fiber over 100 g/100 g is physically impossible for a declared portion. */
export const CATALOG_FIBER_MAX_G = 100;

/**
 * Above this declared salt, the catalog importer requires the product
 * name/category to look like a genuinely salt-forward product (a stock/broth
 * concentrate, a seasoning blend, a brine-cured item); otherwise it is far
 * more likely a mg-as-g unit error (observed in the dataset: pasta, flour,
 * legumes, sorbet all declared with an implausible 20-95 g/100g salt).
 */
export const CATALOG_SALT_HIGH_THRESHOLD_G = 20;

/**
 * Case-insensitive substrings matched against `name + ' ' + category` — only
 * consulted once salt already exceeds `CATALOG_SALT_HIGH_THRESHOLD_G`, so a
 * broad list is safe: it never suppresses the check for an ordinary product,
 * it only recognises the product families where a high salt content is the
 * expected, correctly-declared value (broths/stock concentrates, bouillon
 * cubes, seasoning blends, brine, salt-cured anchovies/capers, condiments).
 */
export const HIGH_SALT_NAME_KEYWORDS: readonly string[] = [
  'brodo',
  'dado',
  'dadi',
  'insaporitore',
  'salamoia',
  'colatura',
  'fumetto',
  'condiment',
  'condi',
  'spezi',
  'miscela',
  'sale',
  'capper',
  'acciug',
  'alici',
  'grill',
  // Granulated/paste stock base ("fondo bruno", "fondo di crostacei", ...):
  // every catalog row matching this word is a concentrated stock, 7-22 g/100g
  // salt across the dataset — a stock base is exactly the product family the
  // 20 g threshold exists to let through.
  'fondo',
  // Wiberg is a professional spice/seasoning-blend and bouillon manufacturer;
  // every catalog row carries the brand name inside `foodName` itself (this
  // 9-column catalog has no separate brand input, so matching the brand
  // substring here is the category-based rule this dataset's shape allows —
  // its own `category` field is the generic "Alimentari" for every Wiberg
  // row, so it cannot distinguish spice blends from anything else). A
  // broader word like "pepe" is deliberately avoided: plain ground pepper has
  // near-zero salt, so a 20+ g salt "pepe" row is a seasoning-salt blend
  // (confirmed for every Wiberg row above the threshold: seasoning salts,
  // marinades, grill rubs, bouillon), never plain pepper.
  'wiberg',
];

export function isHighSaltNameWhitelisted(name: string, category?: string | null): boolean {
  const haystack = `${name} ${category ?? ''}`.toLocaleLowerCase();
  return HIGH_SALT_NAME_KEYWORDS.some((keyword) => haystack.includes(keyword));
}

/**
 * Case-insensitive substrings matched against `name + ' ' + category` to
 * recognise the three product families whose declared kcal legitimately
 * depends on a field this 9-column catalog never carries: alcoholic
 * beverages/spirits (alcohol), sugar-free confectionery/gum (polyols),
 * vinegar (organic acids). Only consulted to EXEMPT
 * `ENERGY_MACRO_MISMATCH`, and only when the corresponding field is genuinely
 * absent (`null`) on the input — a row that DOES declare `alcohol`/`polyols`/
 * `organicAcids` is checked against the real Annex XIV derivation like any
 * other product, exemption or not.
 */
export const ALCOHOL_NAME_KEYWORDS: readonly string[] = [
  'birra',
  'vino',
  'liquore',
  'amaro',
  'grappa',
  'rum',
  'vodka',
  'gin',
  'whisky',
  'whiskey',
  'spumante',
  'prosecco',
  'champagne',
  'cognac',
  'brandy',
  'sidro',
  'sherry',
  'porto',
  'marsala',
  'vermouth',
  'limoncello',
];

export const POLYOL_NAME_KEYWORDS: readonly string[] = [
  'senza zucchero',
  'gomma',
  'gomme',
  'chewing gum',
  'edulcorante',
  'maltitolo',
  'eritritolo',
  'xilitolo',
  'sorbitolo',
  'isomalto',
  // "Zero calorie" (distinct from plural "senza zuccheri", not matched by
  // the singular "senza zucchero" keyword above): a near-zero visible
  // carbohydrate with a much higher declared kcal is exactly the polyol-bulk
  // signature this exemption exists for (e.g. a "zero calorie" syrup whose
  // sweetener bulk is a polyol this catalog never carries as its own field).
  'zero calorie',
  // FRISK: sugar-free mint/candy brand; the brand name is embedded directly
  // in `foodName` (no separate brand input on this 9-column catalog).
  'frisk',
  // Golia "Activ Plus": a sugar-free candy sub-line whose name omits
  // "senza zucchero" wording. Confirmed against the catalog's own
  // "Golia ... senza zucchero" row, which the "senza zucchero" keyword above
  // already exempts and which shares the identical macro profile (carb 93,
  // kcal 230) — kept as the specific sub-line name rather than a bare
  // "activ"/"golia", since an ordinary sugared "Golia" candy (e.g. "Golia
  // frutta C") must keep failing this check.
  'activ plus',
];

export const VINEGAR_NAME_KEYWORDS: readonly string[] = ['aceto'];

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Whole-word match (not a bare substring): a plain `includes` would match
 * "rum" inside "frumento" or "gin" inside "originali" — real false positives
 * hit while tuning this exemption against the catalog (Italian wheat-gluten
 * and biscuit rows). Word-boundary regex avoids that without needing a
 * curated stop-list.
 */
function matchesAnyKeyword(haystack: string, keywords: readonly string[]): boolean {
  return keywords.some((keyword) => new RegExp(`\\b${escapeRegExp(keyword)}\\b`).test(haystack));
}

/**
 * A row is exempt from `ENERGY_MACRO_MISMATCH` when its name/category matches
 * a product family whose energy legitimately depends on a field absent from
 * this catalog (alcohol, polyols, organic acids) AND that field is genuinely
 * undeclared (`null`) on the row — never when the field is present, since
 * then the real Annex XIV derivation already accounts for it.
 */
export function isEnergyMacroMismatchExempt(
  input: Pick<
    CatalogImportPlausibilityInput,
    'name' | 'category' | 'alcohol' | 'polyols' | 'organicAcids'
  >,
): boolean {
  const haystack = `${input.name} ${input.category ?? ''}`.toLocaleLowerCase();
  if (input.alcohol == null && matchesAnyKeyword(haystack, ALCOHOL_NAME_KEYWORDS)) {
    return true;
  }
  if (input.polyols == null && matchesAnyKeyword(haystack, POLYOL_NAME_KEYWORDS)) {
    return true;
  }
  if (input.organicAcids == null && matchesAnyKeyword(haystack, VINEGAR_NAME_KEYWORDS)) {
    return true;
  }
  return false;
}

/** Annex XIV Reg. (UE) 1169/2011 energy conversion factors, kcal per gram. */
export const ENERGY_FACTOR_KCAL_PER_G = {
  fat: 9,
  carbohydrate: 4,
  protein: 4,
  fiber: 2,
  polyols: 2.4,
  alcohol: 7,
  organicAcids: 3,
} as const;

/**
 * Relative tolerance for the declared-vs-macros-derived kcal check, combined
 * with an absolute floor (see below). Tuned against the distributor catalog
 * dataset: at 35% + a 80 kcal floor, the check clears ordinary rounding noise
 * and every product whose energy legitimately depends on a field this
 * dataset does not carry (alcohol in beer/wine, organic acids in vinegar,
 * polyols in sugar-free gum) while still catching every case where the
 * declared kcal and the macro breakdown are simply unrelated numbers (e.g. a
 * frozen-pea product declaring 247 kcal against fat 0.5 + carbohydrate 9 +
 * protein 4.5 + fiber 3 = ~65 kcal derived; a "wheat gluten" row where the
 * protein field was overwritten with the Kjeldahl nitrogen factor 6.25
 * instead of the actual protein grams).
 */
export const CATALOG_ENERGY_RELATIVE_TOLERANCE = 0.35;
export const CATALOG_ENERGY_ABSOLUTE_FLOOR_KCAL = 80;

export interface CatalogImportPlausibilityInput {
  name: string;
  category?: string | null;
  energyKcal: number;
  fat: number;
  carbohydrate: number;
  protein: number;
  fiber?: number | null;
  salt: number;
  polyols?: number | null;
  alcohol?: number | null;
  organicAcids?: number | null;
}

export function derivedEnergyKcalFromMacros(
  input: Pick<
    CatalogImportPlausibilityInput,
    'fat' | 'carbohydrate' | 'protein' | 'fiber' | 'polyols' | 'alcohol' | 'organicAcids'
  >,
): number {
  return (
    input.fat * ENERGY_FACTOR_KCAL_PER_G.fat +
    input.carbohydrate * ENERGY_FACTOR_KCAL_PER_G.carbohydrate +
    input.protein * ENERGY_FACTOR_KCAL_PER_G.protein +
    (input.fiber ?? 0) * ENERGY_FACTOR_KCAL_PER_G.fiber +
    (input.polyols ?? 0) * ENERGY_FACTOR_KCAL_PER_G.polyols +
    (input.alcohol ?? 0) * ENERGY_FACTOR_KCAL_PER_G.alcohol +
    (input.organicAcids ?? 0) * ENERGY_FACTOR_KCAL_PER_G.organicAcids
  );
}

/**
 * Stricter, opt-in checks for bulk catalog curation: fiber<=100,
 * salt<=20 unless the product name/category is a recognised high-salt
 * family, and declared kcal coherent with the Annex XIV macros-derived kcal.
 * See the module docstring for why this is not applied to a single tenant
 * label entry.
 */
export function checkCatalogImportPlausibility(
  input: CatalogImportPlausibilityInput,
): CatalogImportPlausibilityViolation[] {
  const violations: CatalogImportPlausibilityViolation[] = [];
  const fiber = input.fiber ?? 0;

  if (fiber > CATALOG_FIBER_MAX_G) {
    violations.push({
      code: 'FIBER_IMPLAUSIBLE',
      message: `Declared fiber ${fiber} g exceeds the ${CATALOG_FIBER_MAX_G} g portion`,
    });
  }
  if (
    input.salt > CATALOG_SALT_HIGH_THRESHOLD_G &&
    !isHighSaltNameWhitelisted(input.name, input.category)
  ) {
    violations.push({
      code: 'SALT_IMPLAUSIBLE_UNVERIFIED',
      message: `Declared salt ${input.salt} g exceeds ${CATALOG_SALT_HIGH_THRESHOLD_G} g and the product name is not a recognised high-salt family`,
    });
  }
  if (!isEnergyMacroMismatchExempt(input)) {
    const derivedKcal = derivedEnergyKcalFromMacros(input);
    const allowedDelta = Math.max(
      input.energyKcal * CATALOG_ENERGY_RELATIVE_TOLERANCE,
      CATALOG_ENERGY_ABSOLUTE_FLOOR_KCAL,
    );
    if (Math.abs(input.energyKcal - derivedKcal) > allowedDelta) {
      violations.push({
        code: 'ENERGY_MACRO_MISMATCH',
        message: `Declared kcal (${input.energyKcal}) is not coherent with macros-derived kcal (${derivedKcal.toFixed(1)})`,
      });
    }
  }
  return violations;
}
